//제목 목록, 데이터 목록 배열, 각각의 width 인자로 받아서 띄워주기
import styled from "styled-components";
import React, { useState, useEffect } from "react";
import { ReactComponent as PreviousIcon } from "assets/icons/icon-previous-16.svg";
import { ReactComponent as NextIcon } from "assets/icons/icon-next-16.svg";
import { Link } from "react-router-dom";
import { stringSkip } from "common/utils/stringUtil";
import { DataLoadingLayout } from "common/utils/DataLoadingLayout";
import { textStyleBody2 } from "..";

/**
 * 테이블요소로 리스트를 적용한 페이징 함수
 *
 * onePageCnt : 한화면에 보여지는 게시물 수
 * totalCnt : 게시물의 전체 갯수
 * listCallback : 게시물 가져오는 함수
 * */

const ServerPagingListTable = ({
  titleList = [],
  dataList = [],
  widthList = [],
  onePageCnt = 9,
  totalCnt,
  listFunction,
  externalPageSize,
  externalCurrentPage,
  emptyMessage,
  isCellMerge,
  mergeCellIndexs,
  isLoading,
}) => {
  const [currentPage, setCurrentPage] = useState(
    Number(externalCurrentPage) || 1,
  );
  const [totalPage, setTotalPage] = useState(0);
  const [pagingSize, setPagingSize] = useState(Number(externalPageSize) || 10);
  const [startPageIndex, setStartPageIndex] = useState(1);

  const [currentPageList, setCurrentPageList] = useState([]);
  const [currentDataList, setCurrentDataList] = useState([]);

  const computeMergedRows = (dataList, mergeTargetIndexes = []) => {
    const rowCount = dataList.length;
    const colCount = dataList[0]?.length || 0;
    const mergedInfo = Array.from({ length: rowCount }, () =>
      Array(colCount).fill({}),
    );

    for (let col = 0; col < colCount; col++) {
      if (!mergeTargetIndexes.includes(col)) {
        // 병합 대상 아님: 각 셀은 그대로 출력
        for (let row = 0; row < rowCount; row++) {
          mergedInfo[row][col] = { rowSpan: 1 };
        }
        continue;
      }

      let prevValue = null;
      let startIndex = 0;

      for (let row = 0; row < rowCount; row++) {
        const currentValue = dataList[row][col];

        if (
          prevValue ===
          (typeof currentValue === "object" && currentValue?.value
            ? currentValue.value
            : currentValue)
        ) {
          mergedInfo[startIndex][col].rowSpan += 1;
          mergedInfo[row][col] = { skip: true };
        } else {
          mergedInfo[row][col] = { rowSpan: 1 };
          prevValue =
            typeof currentValue === "object" && currentValue?.value
              ? currentValue.value
              : currentValue;
          startIndex = row;
        }
      }
    }

    return mergedInfo;
  };

  const mergedInfo = computeMergedRows(currentDataList, mergeCellIndexs);

  useEffect(() => {
    setTotalPage(Math.ceil(totalCnt / onePageCnt));
    setCurrentDataList(dataList);
  }, [dataList, totalCnt, pagingSize, onePageCnt]);

  useEffect(() => {
    const pageList = [];
    if (startPageIndex !== 1) {
      pageList.push(
        <PageButton
          onClick={() => {
            setCurrentPage(startPageIndex - 1);
            setStartPageIndex(startPageIndex - pagingSize);
            listFunction(currentPage - 1, onePageCnt);
          }}>
          <PreviousIcon></PreviousIcon>
        </PageButton>,
      );
    }

    for (let i = startPageIndex; i < startPageIndex + pagingSize; i++) {
      if (i > totalPage) {
        break;
      }

      pageList.push(
        i !== currentPage ? (
          <PageButton
            onClick={() => {
              setCurrentPage(i);
              listFunction(i);
            }}>
            {i}
          </PageButton>
        ) : (
          <PageButtonSelected
            onClick={() => {
              setCurrentPage(i);
              listFunction(i);
            }}>
            {i}
          </PageButtonSelected>
        ),
      );
    }

    if (startPageIndex * pagingSize <= totalPage) {
      pageList.push(
        <PageButton
          onClick={() => {
            setCurrentPage(startPageIndex * pagingSize + 1);
            setStartPageIndex(startPageIndex * pagingSize + 1);
            listFunction(startPageIndex * pagingSize + 1);
          }}>
          <NextIcon></NextIcon>
        </PageButton>,
      );
    }

    setCurrentPageList(pageList);
  }, [currentPage, onePageCnt, totalPage]);

  return (
    <DataLoadingLayout loading={isLoading}>
      <TableListContainer>
        <TableWrapper>
          <thead>
            <TableRow>
              {titleList.map((title, index) => (
                <TableHeaderCell
                  key={index}
                  style={{ width: widthList[index] }}>
                  {title}
                </TableHeaderCell>
              ))}
            </TableRow>
          </thead>
          {currentDataList === undefined ||
          currentDataList === null ||
          currentDataList.length === 0 ? (
            <TableRow>
              <TableCell colSpan={titleList.length} isLastRow={true}>
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            currentDataList.map((data, rowIdx) => {
              return (
                <TableRow key={rowIdx}>
                  {data.map((item, colIdx) => {
                    let isLeftBorder = true;
                    let info = { rowSpan: 1 };
                    let value =
                      typeof item === "object" && item?.element
                        ? item.element
                        : item;

                    if (isCellMerge) {
                      info = mergedInfo[rowIdx][colIdx];
                      if (info.skip) return null;

                      if (
                        info.rowSpan > 1 &&
                        (colIdx === 0 || // 이 조건을 먼저 확인!
                          mergedInfo[rowIdx][colIdx - 1]?.skip ||
                          mergedInfo[rowIdx][colIdx - 1]?.rowSpan > 1)
                      ) {
                        isLeftBorder = false;
                      }
                    }

                    return (
                      <TableCell
                        key={colIdx}
                        rowSpan={info.rowSpan}
                        isLeftBorder={isLeftBorder}>
                        {value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })
          )}
        </TableWrapper>
        <PageContainer>
          {currentPageList.length === 1 ? (
            <></>
          ) : (
            currentPageList.map((page, index) => page)
          )}
        </PageContainer>
      </TableListContainer>
    </DataLoadingLayout>
  );
};

export const TableListContainer = styled.div`
  width: 100%;
`;
export const TableWrapper = styled.table`
  border: 1px solid var(--Gray, #cfd2d7);
  border-bottom: none;
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
`;

export const TableHeaderCell = styled.th`
  width: ${(props) => props.width || "auto"};
  background: #f0f0f0;
  font-size: 14px;
  border-bottom: 1px solid var(--Gray, #cfd2d7);
  padding: 8px;
`;

// TableCell (td 스타일)
export const TableCell = styled.td`
  ${textStyleBody2}
  width: ${(props) => props.width || "auto"};
  text-align: center;
  border-bottom: 1px solid var(--Gray, #cfd2d7);
  padding: 8px;

  ${({ rowSpan, isLeftBorder }) =>
    rowSpan > 1 &&
    `
     ${isLeftBorder ? `border-left: 1px solid var(--Gray, #CFD2D7);` : ""}
    border-right: 1px solid var(--Gray, #CFD2D7);
    `}
`;

// TableRow (tr 스타일, 필요하면)
export const TableRow = styled.tr`
  height: 44px;
`;

/**
 * onePageCnt : 한화면에 보여지는 게시물 수
 * totalCnt : 게시물의 전체 갯수
 * listCallback : 게시물 가져오는 함수
 * */

/**
 * onePageCnt : 한화면에 보여지는 게시물 수
 * totalCnt : 게시물의 전체 갯수
 * listCallback : 게시물 가져오는 함수
 * */

const ServerPagingList = ({
  titleList = [],
  dataList = [],
  widthList = [],
  onePageCnt = 9,
  totalCnt,
  listFunction,
  externalPageSize,
  externalCurrentPage,
  emptyMessage,
}) => {
  const [currentPage, setCurrentPage] = useState(
    Number(externalCurrentPage) || 1,
  );
  const [totalPage, setTotalPage] = useState(0);
  const [pagingSize, setPagingSize] = useState(Number(externalPageSize) || 10);
  const [startPageIndex, setStartPageIndex] = useState(1);

  const [currentPageList, setCurrentPageList] = useState([]);
  const [currentDataList, setCurrentDataList] = useState([]);

  useEffect(() => {
    setTotalPage(Math.ceil(totalCnt / onePageCnt));
    setCurrentDataList(dataList);
  }, [dataList, totalCnt, pagingSize]);

  useEffect(() => {
    const pageList = [];
    if (startPageIndex !== 1) {
      pageList.push(
        <PageButton
          onClick={() => {
            setCurrentPage(startPageIndex - 1);
            setStartPageIndex(startPageIndex - pagingSize);
            listFunction(currentPage - 1, onePageCnt);
          }}>
          <PreviousIcon></PreviousIcon>
        </PageButton>,
      );
    }

    for (let i = startPageIndex; i < startPageIndex + pagingSize; i++) {
      if (i > totalPage) {
        break;
      }

      pageList.push(
        i !== currentPage ? (
          <PageButton
            onClick={() => {
              setCurrentPage(i);
              listFunction(i);
            }}>
            {i}
          </PageButton>
        ) : (
          <PageButtonSelected
            onClick={() => {
              setCurrentPage(i);
              listFunction(i);
            }}>
            {i}
          </PageButtonSelected>
        ),
      );
    }

    if (startPageIndex * pagingSize <= totalPage) {
      pageList.push(
        <PageButton
          onClick={() => {
            setCurrentPage(startPageIndex * pagingSize + 1);
            setStartPageIndex(startPageIndex * pagingSize + 1);
            listFunction(startPageIndex * pagingSize + 1);
          }}>
          <NextIcon></NextIcon>
        </PageButton>,
      );
    }

    setCurrentPageList(pageList);
  }, [currentPage, onePageCnt, totalPage]);

  return (
    <div
      style={{
        width: "100%",
      }}>
      <ListContainer>
        <ListTitleRow>
          {titleList.map((title, index) => (
            <ListTitleItem key={index} style={{ width: widthList[index] }}>
              {title}
            </ListTitleItem>
          ))}
        </ListTitleRow>
        {currentDataList === undefined ||
        currentDataList === null ||
        currentDataList.length === 0 ? (
          <ListDataLastRow style={{ width: "100%" }}>
            {emptyMessage}
          </ListDataLastRow>
        ) : (
          currentDataList.map((data, index) => {
            return index === currentDataList.length - 1 ? (
              <ListDataLastRow key={index} topBorder={false}>
                {data.map((item, index) => (
                  <ListData key={index} style={{ width: widthList[index] }}>
                    {item}
                  </ListData>
                ))}
              </ListDataLastRow>
            ) : (
              <ListDataRow topBorder={false}>
                {data.map((item, index) => (
                  <ListData key={index} style={{ width: widthList[index] }}>
                    {item}
                  </ListData>
                ))}
              </ListDataRow>
            );
          })
        )}
      </ListContainer>
      <PageContainer>
        {currentPageList.length === 1 ? (
          <></>
        ) : (
          currentPageList.map((page, index) => page)
        )}
      </PageContainer>
    </div>
  );
};

const DefaultList = ({
  titleList = [],
  dataList = [],
  widthList = [],
  onePageCnt = 9,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(
    parseInt((dataList.length + onePageCnt - 1) / onePageCnt),
  );
  const [currentPagePage, setCurrentPagePage] = useState(1); // 1이면 1 ~ 10페이지, 2면 11 ~ 20페이지, ...
  const [currentPageList, setCurrentPageList] = useState([]);
  const [currentDataList, setCurrentDataList] = useState([]);

  useEffect(() => {
    setTotalPage(parseInt((dataList.length + onePageCnt - 1) / onePageCnt));
  }, [dataList, onePageCnt]);

  useEffect(() => {
    var dataListTmp = [],
      pageListTmp = [];

    for (let i = 0; i < onePageCnt; i++) {
      if (dataList[i] === undefined) break;
      dataListTmp.push(dataList[i]);
    }
    setCurrentDataList(dataListTmp);

    for (let i = 1; i <= 10; i++) {
      if (i > totalPage) break;
      pageListTmp.push(
        i !== currentPage ? (
          <PageButton
            onClick={() => {
              setCurrentPage(i);
            }}>
            {i}
          </PageButton>
        ) : (
          <PageButtonSelected
            onClick={() => {
              setCurrentPage(i);
            }}>
            {i}
          </PageButtonSelected>
        ),
      );
    }
    if (totalPage > 10)
      pageListTmp.push(
        <PageButton
          onClick={() => {
            setCurrentPagePage(currentPagePage + 1);
          }}>
          <NextIcon></NextIcon>
        </PageButton>,
      );
    setCurrentPageList(pageListTmp);
  }, [dataList, totalPage, currentPagePage, currentPage, onePageCnt]);

  useEffect(() => {
    var pageListTmp = [];

    if (currentPagePage !== 1)
      pageListTmp.push(
        <PageButton
          onClick={() => {
            setCurrentPagePage(currentPagePage - 1);
          }}>
          <PreviousIcon></PreviousIcon>
        </PageButton>,
      );
    for (
      let i = 1 + (currentPagePage - 1) * 10;
      i <= 10 + (currentPagePage - 1) * 10;
      i++
    ) {
      if (i > totalPage) break;
      pageListTmp.push(
        i !== currentPage ? (
          <PageButton
            onClick={() => {
              setCurrentPage(i);
            }}>
            {i}
          </PageButton>
        ) : (
          <PageButtonSelected
            onClick={() => {
              setCurrentPage(i);
            }}>
            {i}
          </PageButtonSelected>
        ),
      );
    }
    if (totalPage > 10 + (currentPagePage - 1) * 10)
      pageListTmp.push(
        <PageButton
          onClick={() => {
            setCurrentPagePage(currentPagePage + 1);
          }}>
          <NextIcon></NextIcon>
        </PageButton>,
      );
    setCurrentPageList(pageListTmp);
  }, [currentPagePage, totalPage, currentPage]);

  useEffect(() => {
    setCurrentPage(currentPagePage * 10 - 9);
  }, [currentPagePage]);

  useEffect(() => {
    var dataListTmp = [];
    for (
      let i = (currentPage - 1) * onePageCnt;
      i < currentPage * onePageCnt;
      i++
    ) {
      if (dataList[i] === undefined) break;
      dataListTmp.push(dataList[i]);
    }
    setCurrentDataList(dataListTmp);
  }, [currentPage, dataList, onePageCnt]);

  return (
    <div
      style={{
        width: "100%",
      }}>
      <ListContainer
        style={{
          height: 45 * (onePageCnt + 1) + "px",
          minHeight: 45 * (onePageCnt + 1) + "px",
        }}>
        <ListTitleRow>
          {titleList.map((title, index) => (
            <ListTitleItem key={index} style={{ width: widthList[index] }}>
              {title}
            </ListTitleItem>
          ))}
        </ListTitleRow>
        {currentDataList === undefined ||
        currentDataList === null ||
        currentDataList.length === 0 ? (
          <div />
        ) : currentDataList[0].statement !== undefined ? (
          <ListDataLastRow style={{ width: "100%" }}>
            {currentDataList[0].statement}
          </ListDataLastRow>
        ) : (
          currentDataList.map((data, index) => {
            return index === currentDataList.length - 1 ? (
              <ListDataLastRow key={index} topBorder={false}>
                {data.map((item, index) => (
                  <ListData key={index} style={{ width: widthList[index] }}>
                    {typeof item == "string"
                      ? stringSkip({ str: item, length: 60 })
                      : item}
                  </ListData>
                ))}
              </ListDataLastRow>
            ) : (
              <ListDataRow topBorder={false}>
                {data.map((item, index) => (
                  <ListData key={index} style={{ width: widthList[index] }}>
                    {typeof item == "string"
                      ? stringSkip({ str: item, length: 60 })
                      : item}
                  </ListData>
                ))}
              </ListDataRow>
            );
          })
        )}
      </ListContainer>
      <PageContainer>
        {currentPageList.length === 1 ? (
          <></>
        ) : (
          currentPageList.map((page, index) => page)
        )}
      </PageContainer>
    </div>
  );
};

const DefaultListWithoutPage = ({ titleList, dataList, widthList }) => {
  return (
    <ListContainer>
      <ListTitleRow>
        {titleList.map((title, index) => (
          <ListTitleItem key={index} style={{ width: widthList[index] }}>
            {title}
          </ListTitleItem>
        ))}
      </ListTitleRow>
      {dataList.map((data, index) => {
        return index === dataList.length - 1 ? (
          <ListDataLastRow key={index}>
            {data.map((item, index) => (
              <ListData key={index} style={{ width: widthList[index] }}>
                {typeof item == "string"
                  ? stringSkip({ str: item, length: 25 })
                  : item}
              </ListData>
            ))}
          </ListDataLastRow>
        ) : (
          <ListDataRow>
            {data.map((item, index) => (
              <ListData key={index} style={{ width: widthList[index] }}>
                {typeof item == "string"
                  ? stringSkip({ str: item, length: 25 })
                  : item}
              </ListData>
            ))}
          </ListDataRow>
        );
      })}
    </ListContainer>
  );
};

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  width: 100%;
  padding: 0;
  margin: 10px 0 0 0;
`;

const ListRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;

  width: calc(100% - var(--border-width) * 2);
  height: 44px;
  min-height: 44px;
  padding: 0;

  border-top: ${(props) =>
    props.topBorder === false
      ? "none"
      : `var(--border-width) solid var(--Gray)`}; /* 상단 border */
  border-left: var(--border-width) solid var(--Gray); /* 왼쪽 border */
  border-right: var(--border-width) solid var(--Gray); /* 오른쪽 border */
  border-bottom: var(--border-width) solid var(--Gray); /* 하단 border */

  font-size: var(--font-size-14);
  font-color: var(--Black);
`;

const ListTitleRow = styled(ListRow)`
  background-color: var(--LightGray);
  border-radius: var(--border-radius-8) var(--border-radius-8) 0 0;
  font-weight: var(--font-weight-bold);
`;

const ListDataRow = styled(ListRow)`
  background-color: var(--White);
  font-weight: var(--font-weight-normal);
`;

const ListDataLastRow = styled(ListDataRow)`
  border-radius: 0 0 var(--border-radius-8) var(--border-radius-8);
`;

const ListTitleItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const ListData = styled.div`
  display: flex;
  flex-direction: column;
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;

  width: 100%;
  height: 20px;
  min-height: 20px;
  margin: 15px 0 0 0;
`;

const PageButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: end;
  text-align: center;

  width: 25px;
  height: 100%;

  color: var(--LightNavy);
  font-size: var(--font-size-14);

  cursor: pointer;
`;

const PageButtonSelected = styled(PageButton)`
  color: var(--MainBlue);
  font-size: var(--font-size-14);
  font-weight: var(--font-weight-bold);
`;

const ReservationCustomList = ({ titleList, dataList, widthList, id }) => {
  return (
    <CustomListContainer id={id === null ? "" : id}>
      <CustomListTitleRow>
        {titleList.map((title, index) =>
          index !== titleList.length - 1 ? (
            <CustomListTitleItem
              key={"CustomListTitleItem" + index}
              style={{ width: widthList[index] }}>
              {title}
            </CustomListTitleItem>
          ) : (
            <CustomListTitleItem
              key={"CustomListTitleItem" + index}
              style={{ width: widthList[widthList.length - 1][0] }}>
              {titleList[titleList.length - 1][1].map((title, index) => (
                <CustomListTitleItem
                  key={"CustomListTitleItem" + index}
                  style={{ width: widthList[widthList.length - 1][1][index] }}>
                  {title}
                </CustomListTitleItem>
              ))}
            </CustomListTitleItem>
          ),
        )}
      </CustomListTitleRow>
      {dataList.map((data, index) => {
        return index === dataList.length - 1 ? (
          <CustomListDataLastRow key={index}>
            {data.map((item1, index) =>
              index !== dataList[0].length - 1 ? (
                <CustomListData
                  key={"CustomListData" + index}
                  style={{
                    width: widthList[index],
                    height: data[dataList[0].length - 1].length * 45 - 1 + "px",
                    borderLeft: index !== 0 ? "1px solid var(--Gray)" : "none",
                  }}>
                  {item1}
                </CustomListData>
              ) : (
                <CustomListData
                  key={"CustomListData" + index}
                  style={{ width: widthList[index][0] }}>
                  <CustomListDataSmallRows>
                    {item1.map((item2, index) => (
                      <CustomListLink
                        key={"CustomListLink" + index}
                        to="/reservation-detail"
                        state={item2[item2.length - 1]}>
                        <CustomListDataSmallRow
                          key={"CustomListDataSmallRow" + index}
                          style={{
                            borderTop:
                              index === 0 ? "none" : "1px solid var(--Gray)",
                          }}>
                          {item2.map((item3, index) => {
                            return index > 5 ? (
                              <React.Fragment
                                key={"React.Fragment" + index}></React.Fragment>
                            ) : (
                              <CustomListData
                                key={"CustomListData" + index}
                                style={{
                                  width:
                                    widthList[widthList.length - 1][1][index],
                                  backgroundColor:
                                    index === 0
                                      ? item2[6] === "Offline"
                                        ? "var(--LightOrange)"
                                        : item2[6] === "Online"
                                          ? "var(--LightGreen)"
                                          : "var(--White)"
                                      : index === 5
                                        ? "transparent"
                                        : "var(--White)",
                                  color:
                                    index === 0
                                      ? item2[6] === "Available"
                                        ? "var(--Blue)"
                                        : item2[6] === "Offline Reservation"
                                          ? "var(--Red)"
                                          : "var(--Black)"
                                      : "var(--Black)",
                                  fontWeight:
                                    index === 0
                                      ? item2[6] === "Available" ||
                                        item2[6] === "Offline Reservation"
                                        ? "var(--font-weight-bold)"
                                        : "var(--font-weight-normal)"
                                      : "var(--font-weight-normal)",
                                }}>
                                {item3}
                              </CustomListData>
                            );
                          })}
                        </CustomListDataSmallRow>
                      </CustomListLink>
                    ))}
                  </CustomListDataSmallRows>
                </CustomListData>
              ),
            )}
          </CustomListDataLastRow>
        ) : (
          <CustomListDataRow key={index}>
            {data.map((item1, index) =>
              index !== dataList[0].length - 1 ? (
                <CustomListData
                  key={"CustomListData" + index}
                  style={{
                    width: widthList[index],
                    height: data[dataList[0].length - 1].length * 45 - 1 + "px",
                    borderLeft: index !== 0 ? "1px solid var(--Gray)" : "none",
                  }}>
                  {item1}
                </CustomListData>
              ) : (
                <CustomListData
                  key={index}
                  style={{ width: widthList[index][0] }}>
                  <CustomListDataSmallRows>
                    {item1.map((item2, index) => (
                      <CustomListLink
                        key={"CustomListLink" + index}
                        to="/reservation-detail"
                        state={item2[item2.length - 1]}>
                        <CustomListDataSmallRow
                          key={"CustomListDataSmallRow" + index}
                          style={{
                            borderTop:
                              index === 0 ? "none" : "1px solid var(--Gray)",
                          }}>
                          {item2.map((item3, index) => {
                            return index > 5 ? (
                              <React.Fragment
                                key={"React.Fragment" + index}></React.Fragment>
                            ) : (
                              <CustomListData
                                key={"CustomListData" + index}
                                style={{
                                  width:
                                    widthList[widthList.length - 1][1][index],
                                  backgroundColor:
                                    index === 0
                                      ? item2[6] === "Offline"
                                        ? "var(--LightOrange)"
                                        : item2[6] === "Online"
                                          ? "var(--LightGreen)"
                                          : "var(--White)"
                                      : index === 5
                                        ? "transparent"
                                        : "var(--White)",
                                  color:
                                    index === 0
                                      ? item2[6] === "Available"
                                        ? "var(--Blue)"
                                        : item2[6] === "Offline Reservation"
                                          ? "var(--Red)"
                                          : "var(--Black)"
                                      : "var(--Black)",
                                  fontWeight:
                                    index === 0
                                      ? item2[6] === "Available" ||
                                        item2[6] === "Offline Reservation"
                                        ? "var(--font-weight-bold)"
                                        : "var(--font-weight-normal)"
                                      : "var(--font-weight-normal)",
                                }}>
                                {item3}
                              </CustomListData>
                            );
                          })}
                        </CustomListDataSmallRow>
                      </CustomListLink>
                    ))}
                  </CustomListDataSmallRows>
                </CustomListData>
              ),
            )}
          </CustomListDataRow>
        );
      })}
    </CustomListContainer>
  );
};

const CustomListContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  position: sticky;
  top: 0;

  width: 100%;
  padding: 0;
`;

const CustomListRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;

  width: calc(100% - var(--border-width) * 2);
  padding: 0;
  border: var(--border-width) solid var(--Gray);

  font-size: var(--font-size-14);
  font-color: var(--Black);
`;

const CustomListTitleRow = styled(CustomListRow)`
  background-color: var(--LightGray);
  position: sticky;
  top: 0;
  min-height: 44px;

  border-radius: var(--border-radius-8) var(--border-radius-8) 0 0;

  font-weight: var(--font-weight-bold);
`;

const CustomListDataRow = styled(CustomListRow)`
  background-color: var(--White);

  font-weight: var(--font-weight-normal);
`;

const CustomListDataLastRow = styled(CustomListDataRow)`
  border-radius: 0 0 var(--border-radius-8) var(--border-radius-8);
`;

const CustomListTitleItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const CustomListDataSmallRows = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;

  width: 100%;

  border-left: var(--border-width) solid var(--Gray);
`;

const CustomListDataSmallRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;

  width: 100%;
  height: 44px;
  min-height: 44px;
`;

const CustomListLink = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 100%;

  text-decoration: none;
`;

const CustomListData = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
`;

export {
  DefaultList,
  DefaultListWithoutPage,
  ReservationCustomList,
  ServerPagingList,
  ServerPagingListTable,
};
